import { useEffect, useState, type ReactNode } from 'react';
import { EV } from '@shared/protocol';
import { useUIStore } from '@/stores/uiStore';
import { useAuthStore } from '@/auth/authStore';
import { useClanStore } from '@/stores/clanStore';
import { useClanNotifStore } from '@/stores/clanNotifStore';
import { useUserStore } from '@/stores/userStore';
import { isImageAssetUrl, resolveAssetUrl } from '@/shop/assetUrl';
import { useMatchStore } from '@/stores/matchStore';
import { useToasts } from '@/stores/toastStore';
import { confirm } from '@/stores/confirmStore';
import { getSocket } from '@/net/socket';
import { Avatar } from '@/art';
import { BackButton } from '@/components/BackButton';
import { useT } from '@/i18n/useI18n';
import { containsProfanity } from '@shared/profanity';
import type { I18nKey } from '@/i18n/translations';
import {
  ClanApiError,
  clanApi,
  type ActiveWarDTO,
  type ClanDetailDTO,
  type ClanLeaderboardEntry,
  type ClanMemberDTO,
  type ClanRole,
  type ClanSeasonDTO,
  type ClanSummaryDTO,
  type ClanJoinPolicy,
  type WarSideDTO,
  type WarRegStatus,
  type CreateRequirements,
  type ClanEmblemItem,
} from '@/clan/clanApi';

type T = (key: I18nKey, vars?: Record<string, string | number>) => string;

const ROLE_RANK: Record<ClanRole, number> = { leader: 3, coLeader: 2, elder: 1, member: 0 };
const ROLE_KEY: Record<ClanRole, I18nKey> = {
  leader: 'clan.role.leader',
  coLeader: 'clan.role.coLeader',
  elder: 'clan.role.elder',
  member: 'clan.role.member',
};
const POLICY_KEY: Record<ClanJoinPolicy, I18nKey> = {
  open: 'clan.policy_short_open',
  request: 'clan.policy_short_request',
  invite: 'clan.policy_short_invite',
};

const EMBLEMS = ['🛡️', '⚔️', '🔥', '🐉', '🦁', '🦅', '👑', '💀', '⭐', '🐺', '🦈', '🐢', '🌋', '⚡', '❄️', '🎯'];

function toastErr(e: unknown, t: T): void {
  let msg = t('clan.err_generic');
  if (e instanceof ClanApiError) {
    msg = e.code === 'PROFANITY' ? t('clan.err_profanity') : e.message;
  }
  useToasts.getState().push({ kind: 'warn', title: t('clan.toast_title'), body: msg });
}

export function ClanScreen() {
  const t = useT();
  const go = useUIStore((s) => s.go);
  const myClan = useClanStore((s) => s.myClan);
  const enabled = useClanStore((s) => s.enabled);
  const featureChecked = useClanStore((s) => s.featureChecked);
  const loadAvailability = useClanStore((s) => s.loadAvailability);
  const refresh = useClanStore((s) => s.refresh);
  const refreshWar = useClanStore((s) => s.refreshWar);
  const [showBoard, setShowBoard] = useState(false);

  useEffect(() => { void loadAvailability(); }, [loadAvailability]);

  useEffect(() => {
    if (featureChecked && !enabled) go('home');
  }, [enabled, featureChecked, go]);

  useEffect(() => {
    if (featureChecked && enabled) void refresh();
  }, [enabled, featureChecked, refresh]);

  // Opening the clan screen acknowledges any new clan activity → clear the Home
  // red dot (locally + server-side). The dot is the only surface now (no inbox).
  useEffect(() => {
    if (!enabled) return;
    if (useClanNotifStore.getState().unread === 0) return;
    useClanNotifStore.getState().markAllRead();
    void clanApi.markNotificationsRead().catch(() => { /* best-effort */ });
  }, [enabled]);

  // Poll the war/registration state while in a clan — covers the between-battle
  // window too, so the register card, countdown, and the flip to a live battle
  // all refresh without leaving the screen.
  useEffect(() => {
    if (!enabled || !myClan) return;
    const id = window.setInterval(() => void refreshWar(), 5000);
    return () => window.clearInterval(id);
  }, [enabled, myClan, refreshWar]);

  if (!featureChecked || !enabled) {
    return (
      <div className="screen">
        <div className="hex-bg" />
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-muted)' }}>
          {t('common.loading')}
        </div>
      </div>
    );
  }

  return (
    <div className="screen">
      <div className="hex-bg" />
      <div style={{ padding: '14px 16px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <BackButton onClick={() => (showBoard ? setShowBoard(false) : go('home'))} />
          <div className="display shadow-text" style={{ fontSize: 28, marginTop: 10 }}>
            {showBoard ? t('clan.ranks_title') : t('clan.title')}
          </div>
        </div>
        {!showBoard && (
          <button className="clan-btn-soft" style={{ marginTop: 6 }} onClick={() => setShowBoard(true)}>
            {t('clan.ranks')}
          </button>
        )}
      </div>

      {showBoard ? (
        <LeaderboardView t={t} />
      ) : myClan === undefined ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-muted)' }}>{t('common.loading')}</div>
      ) : myClan === null ? (
        <NoClanView t={t} />
      ) : (
        <ClanProfile clan={myClan} t={t} />
      )}
    </div>
  );
}

function fmtCountdown(targetMs: number, t: T): string {
  const ms = targetMs - Date.now();
  if (ms <= 0) return t('clan.now');
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  return h >= 1 ? `${h}h ${m}m` : `${m}m`;
}

function fmtLeft(targetMs: number, t: T): string {
  const ms = targetMs - Date.now();
  if (ms <= 0) return t('clan.season_ending');
  const d = Math.floor(ms / 86_400_000);
  if (d >= 1) return t('clan.season_left', { t: `${d}d` });
  return t('clan.season_left', { t: `${Math.floor(ms / 3_600_000)}h` });
}

function StatChips({ c, t }: { c: ClanSummaryDTO; t: T }) {
  return (
    <div className="clan-chips">
      <span className="clan-chip is-gold">🏆 {c.trophies}</span>
      <span className="clan-chip">👥 {c.memberCount}/{c.maxMembers}</span>
      <span className="clan-chip">{t(POLICY_KEY[c.joinPolicy])}</span>
    </div>
  );
}

/** Bottom-sheet modal for long lists (members, join requests) so the clan
 * screen stays compact instead of scrolling forever. */
function ClanModal({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  return (
    <div className="clan-modal-overlay" onClick={onClose}>
      <div className="clan-modal" onClick={(e) => e.stopPropagation()}>
        <div className="clan-modal__head">
          <span className="clan-modal__title">{title}</span>
          <button className="clan-modal__close" onClick={onClose} aria-label="close">✕</button>
        </div>
        <div className="clan-modal__body">{children}</div>
      </div>
    </div>
  );
}

// ── No clan yet: browse + create ───────────────────────────────────────────

function NoClanView({ t }: { t: T }) {
  const [tab, setTab] = useState<'browse' | 'create'>('browse');
  return (
    <div className="clan-scroll">
      <div className="clan-seg">
        {(['browse', 'create'] as const).map((k) => (
          <button key={k} className={`clan-seg-item${tab === k ? ' is-active' : ''}`} onClick={() => setTab(k)}>
            {k === 'browse' ? t('clan.find') : t('clan.create')}
          </button>
        ))}
      </div>
      {tab === 'browse' ? <BrowseList t={t} /> : <CreateForm t={t} />}
    </div>
  );
}

function BrowseList({ t }: { t: T }) {
  const join = useClanStore((s) => s.join);
  const pendingRequestId = useClanStore((s) => s.pendingRequestId);
  const cancelJoin = useClanStore((s) => s.cancelJoin);
  const [items, setItems] = useState<ClanSummaryDTO[] | null>(null);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  const load = (query?: string) =>
    clanApi.browse(query).then((r) => setItems(r.items)).catch((e) => toastErr(e, t));
  useEffect(() => { void load(); }, []);

  const onJoin = async (c: ClanSummaryDTO) => {
    setBusy(c.id);
    try {
      const joined = await join(c.id, undefined);
      useToasts.getState().push({
        kind: 'success',
        title: joined ? t('clan.joined') : t('clan.request_sent'),
        body: joined ? t('clan.welcome', { name: c.name }) : t('clan.request_waiting', { name: c.name }),
      });
    } catch (e) { toastErr(e, t); }
    finally { setBusy(null); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          className="clan-input" style={{ marginTop: 0 }}
          value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('clan.search_ph')}
          onKeyDown={(e) => { if (e.key === 'Enter') void load(q); }}
        />
        <button className="clan-btn" onClick={() => void load(q)}>{t('clan.search')}</button>
      </div>

      {items === null ? (
        <div style={{ color: 'var(--ink-muted)', padding: 16 }}>{t('common.loading')}</div>
      ) : items.length === 0 ? (
        <div style={{ color: 'var(--ink-muted)', padding: 24, textAlign: 'center' }}>{t('clan.none_found')}</div>
      ) : (
        items.map((c) => {
          const full = c.memberCount >= c.maxMembers;
          const requested = pendingRequestId === c.id;
          return (
            <div key={c.id} className="clan-row">
              <ClanEmblem emblem={c.emblem} size={44} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div><span className="clan-row__name">{c.name}</span> <span className="clan-row__tag">[{c.tag}]</span></div>
                <StatChips c={c} t={t} />
              </div>
              {requested ? (
                <button className="clan-btn-soft" onClick={() => void cancelJoin(c.id)}>{t('common.cancel')}</button>
              ) : (
                <button className="clan-btn" disabled={full || busy === c.id} onClick={() => void onJoin(c)}>
                  {full ? t('clan.full') : c.joinPolicy === 'open' ? t('clan.join') : t('clan.request')}
                </button>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}

function CreateForm({ t }: { t: T }) {
  const create = useClanStore((s) => s.create);
  const [name, setName] = useState('');
  const [tag, setTag] = useState('');
  const [description, setDescription] = useState('');
  const [emblem, setEmblem] = useState(EMBLEMS[0]!);
  const [joinPolicy, setJoinPolicy] = useState<ClanJoinPolicy>('request');
  const [busy, setBusy] = useState(false);
  const [reqs, setReqs] = useState<CreateRequirements | null>(null);

  useEffect(() => { clanApi.createRequirements().then(setReqs).catch(() => {}); }, []);

  const submit = async () => {
    // Client-side profanity guard for instant feedback (server re-checks).
    if (containsProfanity(name) || containsProfanity(tag) || containsProfanity(description)) {
      useToasts.getState().push({ kind: 'warn', title: t('clan.toast_title'), body: t('clan.err_profanity') });
      return;
    }
    setBusy(true);
    try {
      const clan = await create({
        name: name.trim(), tag: tag.trim().toUpperCase(),
        description: description.trim() || undefined, joinPolicy, emblem,
      });
      useToasts.getState().push({ kind: 'success', title: t('clan.created'), body: clan.name });
    } catch (e) { toastErr(e, t); }
    finally { setBusy(false); }
  };

  return (
    <div className="clan-card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <ClanEmblem emblem={emblem} size={52} />
        <label style={{ flex: 1 }}>
          <span className="clan-field">{t('clan.name_label')}</span>
          <input className="clan-input" value={name} maxLength={20} onChange={(e) => setName(e.target.value)} />
        </label>
      </div>
      <label>
        <span className="clan-field">{t('clan.tag_label')}</span>
        <input className="clan-input" value={tag} maxLength={6} onChange={(e) => setTag(e.target.value.toUpperCase())} />
      </label>
      <div>
        <span className="clan-field">{t('clan.emblem_label')}</span>
        <div style={{ marginTop: 6 }}><EmblemGrid value={emblem} onPick={setEmblem} /></div>
      </div>
      <label>
        <span className="clan-field">{t('clan.desc_label')}</span>
        <textarea className="clan-textarea" value={description} maxLength={200} rows={2} onChange={(e) => setDescription(e.target.value)} style={{ resize: 'vertical' }} />
      </label>
      <label>
        <span className="clan-field">{t('clan.policy_label')}</span>
        <select className="clan-select" value={joinPolicy} onChange={(e) => setJoinPolicy(e.target.value as ClanJoinPolicy)}>
          <option value="open">{t('clan.policy_open')}</option>
          <option value="request">{t('clan.policy_request')}</option>
          <option value="invite">{t('clan.policy_invite')}</option>
        </select>
      </label>
      {reqs && (
        <div className="clan-reqs">
          <div className="clan-label" style={{ margin: '0 0 6px' }}>{t('clan.req_title')}</div>
          <ReqRow ok={reqs.level >= reqs.minLevel} label={t('clan.req_level', { n: reqs.minLevel })} have={`Lv.${reqs.level}`} />
          <ReqRow ok={reqs.coins >= reqs.costCoins} label={t('clan.req_coins', { n: reqs.costCoins })} have={`${reqs.coins}`} />
          {reqs.costGems > 0 && (
            <ReqRow ok={reqs.gems >= reqs.costGems} label={t('clan.req_gems', { n: reqs.costGems })} have={`${reqs.gems}`} />
          )}
        </div>
      )}
      <button
        className="clan-btn clan-btn--full"
        disabled={busy || name.trim().length < 3 || tag.trim().length < 2 || (reqs != null && !reqs.canCreate)}
        onClick={() => void submit()}
      >
        {busy ? t('clan.creating') : t('clan.create_btn')}
      </button>
      <div style={{ fontSize: 10, color: 'var(--ink-dim)', textAlign: 'center' }}>{t('clan.create_note')}</div>
    </div>
  );
}

function ReqRow({ ok, label, have }: { ok: boolean; label: string; have: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12, padding: '3px 0', color: ok ? 'var(--ink)' : 'var(--c-red)' }}>
      <span>{ok ? '✓' : '✗'} {label}</span>
      <span style={{ fontFamily: 'var(--f-mono)', opacity: 0.85 }}>{have}</span>
    </div>
  );
}

function EmblemGrid({ value, onPick }: { value: string; onPick: (e: string) => void }) {
  return (
    <div className="clan-emgrid">
      {EMBLEMS.map((e) => (
        <button key={e} className={value === e ? 'is-sel' : ''} onClick={() => onPick(e)}>{e}</button>
      ))}
    </div>
  );
}

/** Premium clan-logo shop: buy with gems (officer's gems), equip clan-wide. */
function EmblemShop({ clanId, equipped, t }: { clanId: string; equipped: string | null; t: T }) {
  const [items, setItems] = useState<ClanEmblemItem[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const refreshClan = useClanStore((s) => s.refresh);
  const setProfile = useUserStore((s) => s.setProfile);

  const load = (): void => { void clanApi.emblems(clanId).then((r) => setItems(r.items)).catch(() => {}); };
  useEffect(() => { load(); }, []);

  const buy = async (it: ClanEmblemItem): Promise<void> => {
    setBusy(it.id);
    try {
      const r = await clanApi.buyEmblem(clanId, it.id);
      setProfile({ gems: r.gems });
      useToasts.getState().push({ kind: 'success', title: t('clan.emblem_bought'), body: it.name });
      load();
    } catch (e) { toastErr(e, t); }
    finally { setBusy(null); }
  };
  const equip = async (it: ClanEmblemItem): Promise<void> => {
    setBusy(it.id);
    try {
      await clanApi.equipEmblem(clanId, it.id);
      await refreshClan();
      load();
    } catch (e) { toastErr(e, t); }
    finally { setBusy(null); }
  };

  if (items.length === 0) return null;
  return (
    <div style={{ marginTop: 14 }}>
      <div className="clan-label">{t('clan.emblem_shop')}</div>
      <div className="clan-emshop">
        {items.map((it) => {
          const isOn = equipped === it.imageUrl;
          return (
            <div key={it.id} className={`clan-emshop__item rarity-${it.rarity}`}>
              <img className="clan-emshop__img" src={resolveAssetUrl(it.imageUrl) ?? ''} alt={it.name} />
              <div className="clan-emshop__name">{it.name}</div>
              {it.owned ? (
                <button className="clan-emshop__equip" disabled={isOn || busy === it.id} onClick={() => void equip(it)}>
                  {isOn ? t('clan.emblem_equipped') : t('clan.emblem_equip')}
                </button>
              ) : it.special ? (
                <span className="clan-emshop__reward">🏆 {t('clan.emblem_reward')}</span>
              ) : (
                <button className="clan-btn" disabled={busy === it.id} onClick={() => void buy(it)}>
                  💎 {it.priceGems}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── In a clan: profile + roster + war + requests ───────────────────────────

function ClanProfile({ clan, t }: { clan: ClanDetailDTO; t: T }) {
  const leave = useClanStore((s) => s.leave);
  const disband = useClanStore((s) => s.disband);
  const update = useClanStore((s) => s.update);
  const war = useClanStore((s) => s.war);
  const warReg = useClanStore((s) => s.warReg);
  const [pickEmblem, setPickEmblem] = useState(false);
  const [modal, setModal] = useState<null | 'members' | 'requests'>(null);
  const myRole = clan.myRole ?? 'member';
  const isLeader = myRole === 'leader';
  const isOfficer = ROLE_RANK[myRole] >= ROLE_RANK.elder;
  const canEdit = ROLE_RANK[myRole] >= ROLE_RANK.coLeader;

  const onLeave = async () => {
    if (!(await confirm({ title: t('clan.leave'), body: isLeader ? t('clan.confirm_leave_leader') : t('clan.confirm_leave'), danger: true }))) return;
    try { await leave(); useToasts.getState().push({ kind: 'info', title: t('clan.left'), body: clan.name }); }
    catch (e) { toastErr(e, t); }
  };
  const onDisband = async () => {
    if (!(await confirm({ title: t('clan.disband'), body: t('clan.confirm_disband', { name: clan.name }), danger: true }))) return;
    try { await disband(); useToasts.getState().push({ kind: 'info', title: t('clan.disbanded') }); }
    catch (e) { toastErr(e, t); }
  };
  const onEmblem = async (e: string) => {
    setPickEmblem(false);
    try { await update({ emblem: e }); } catch (err) { toastErr(err, t); }
  };

  return (
    <div className="clan-scroll">
      {/* Hero */}
      <div className="clan-card" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <ClanEmblem emblem={clan.emblem} size={60} tappable={canEdit} onClick={() => canEdit && setPickEmblem((v) => !v)} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
            <span className="display" style={{ fontSize: 20 }}>{clan.name}</span>
            <span className="clan-row__tag">[{clan.tag}]</span>
          </div>
          <StatChips c={clan} t={t} />
          {clan.description && <div style={{ fontSize: 12, marginTop: 6, color: 'var(--ink-muted)' }}>{clan.description}</div>}
        </div>
      </div>

      {canEdit && pickEmblem && (
        <div className="clan-card">
          <div className="clan-label">{t('clan.emblem_label')}</div>
          <EmblemGrid value={clan.emblem ?? ''} onPick={(e) => void onEmblem(e)} />
          <EmblemShop clanId={clan.id} equipped={clan.emblem ?? null} t={t} />
        </div>
      )}

      {war ? <WarBoard war={war} t={t} /> : warReg && <WarRegCard reg={warReg} t={t} />}

      {isOfficer && clan.joinRequests && clan.joinRequests.length > 0 && (
        <button className="clan-card clan-rowbtn" onClick={() => setModal('requests')}>
          <span className="clan-label" style={{ color: 'var(--c-cyan)', margin: 0 }}>
            {t('clan.requests_title', { n: clan.joinRequests.length })}
          </span>
          <span className="clan-rowbtn__chev">›</span>
        </button>
      )}

      <button className="clan-card clan-rowbtn" onClick={() => setModal('members')}>
        <span className="clan-label" style={{ color: 'var(--c-yellow)', margin: 0 }}>
          {t('clan.members_title')} · {clan.memberCount}/{clan.maxMembers}
        </span>
        <span className="clan-rowbtn__chev">›</span>
      </button>

      {modal === 'requests' && clan.joinRequests && clan.joinRequests.length > 0 && (
        <ClanModal title={t('clan.requests_title', { n: clan.joinRequests.length })} onClose={() => setModal(null)}>
          <div className="clan-list">
            {clan.joinRequests.map((r) => <RequestRow key={r.userId} req={r} t={t} />)}
          </div>
        </ClanModal>
      )}
      {modal === 'members' && (
        <ClanModal title={`${t('clan.members_title')} · ${clan.memberCount}/${clan.maxMembers}`} onClose={() => setModal(null)}>
          <div className="clan-list">
            {clan.members.map((m) => <MemberRow key={m.userId} member={m} myRole={myRole} t={t} />)}
          </div>
        </ClanModal>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <button className="clan-btn-soft" style={{ flex: 1, textAlign: 'center' }} onClick={() => void onLeave()}>{t('clan.leave')}</button>
        {isLeader && (
          <button className="clan-btn-soft is-danger" style={{ flex: 1, textAlign: 'center' }} onClick={() => void onDisband()}>{t('clan.disband')}</button>
        )}
      </div>
    </div>
  );
}

/** Local HH:mm for a ms timestamp. */
function fmtClock(ms: number): string {
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** Between battles: shows the daily registration state + register button. */
function WarRegCard({ reg, t }: { reg: WarRegStatus; t: T }) {
  const registerWar = useClanStore((s) => s.registerWar);
  const [busy, setBusy] = useState(false);
  const battleClock = `${fmtClock(reg.battleStartAt)}–${fmtClock(reg.battleEndAt)}`;

  const onRegister = async (): Promise<void> => {
    setBusy(true);
    try {
      await registerWar();
      useToasts.getState().push({ kind: 'success', title: t('clan.war.title'), body: t('clan.war.registered_ok') });
    } catch (e) { toastErr(e, t); }
    finally { setBusy(false); }
  };

  return (
    <div className="clan-war">
      <div className="clan-war__head">
        <span className="clan-war__title">⚔️ {t('clan.war.title')}</span>
        <span className="clan-war__phase">{t('clan.war.daily_at', { t: battleClock })}</span>
      </div>

      {reg.registered ? (
        <div style={{ textAlign: 'center', padding: '6px 0' }}>
          <div style={{ color: 'var(--c-cyan)', fontWeight: 700, fontSize: 14 }}>✓ {t('clan.war.registered')}</div>
          <div style={{ fontSize: 12, color: 'var(--ink-muted)', marginTop: 4 }}>
            {t('clan.war.battle_at', { t: fmtClock(reg.battleStartAt) })}
          </div>
        </div>
      ) : reg.canRegister ? (
        <button className="clan-btn clan-btn--full" disabled={busy} onClick={() => void onRegister()}>
          {busy ? t('clan.creating') : t('clan.war.register_btn')}
        </button>
      ) : (
        <div style={{ fontSize: 12, color: 'var(--ink-muted)', textAlign: 'center', padding: '4px 0' }}>
          {reg.memberCount < reg.minMembers
            ? t('clan.war.need_members', { n: reg.minMembers })
            : reg.windowOpen
              ? t('clan.war.await_leader')
              : t('clan.war.reg_opens', { t: fmtClock(reg.regOpensAt) })}
        </div>
      )}
    </div>
  );
}

function WarBoard({ war, t }: { war: ActiveWarDTO; t: T }) {
  const go = useUIStore((s) => s.go);
  const youWinning = war.you.score > war.rival.score;
  const phase = war.state === 'prep'
    ? t('clan.war.prep', { t: fmtCountdown(war.battleStartAt, t) })
    : t('clan.war.battle', { t: fmtCountdown(war.endAt, t) });

  const startWarMatch = (): void => {
    useMatchStore.setState({ matchSource: 'war', warId: war.id });
    try { getSocket().emit(EV.WAR_QUEUE_JOIN, { warId: war.id }); } catch { /* */ }
    go('matching');
  };

  return (
    <div className="clan-war">
      <div className="clan-war__head">
        <span className="clan-war__title">⚔️ {t('clan.war.title')}</span>
        <span className={`clan-war__phase${war.state === 'battle' ? ' is-live' : ''}`}>{phase}</span>
      </div>

      <div className="clan-war__scores">
        <WarScoreSide side={war.you} highlight={youWinning} align="left" />
        <span className="clan-war__vs">{t('clan.war.vs')}</span>
        <WarScoreSide side={war.rival} highlight={!youWinning && war.rival.score > war.you.score} align="right" />
      </div>

      {war.state === 'battle' ? (
        <button className="clan-btn clan-btn--full" disabled={!war.canAttack} onClick={startWarMatch}>
          {war.canAttack
            ? t('clan.war.find', { n: war.attacksPerMember - war.myAttacksUsed })
            : war.myAttacksUsed >= war.attacksPerMember ? t('clan.war.no_attacks') : t('clan.war.not_open')}
        </button>
      ) : (
        <div style={{ fontSize: 11, color: 'var(--ink-muted)', textAlign: 'center' }}>{t('clan.war.prep_note', { n: war.attacksPerMember })}</div>
      )}

      {war.you.contributors.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div className="clan-label" style={{ fontSize: 9, marginBottom: 4 }}>{t('clan.war.top')}</div>
          {war.you.contributors.slice(0, 5).map((c) => (
            <div key={c.userId} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0' }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.displayName}</span>
              <span style={{ color: 'var(--c-yellow)', fontWeight: 700 }}>{t('clan.war.stat', { pt: c.points, wins: c.wins })}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function WarScoreSide({ side, highlight, align }: { side: WarSideDTO; highlight: boolean; align: 'left' | 'right' }) {
  return (
    <div className="clan-war__side" style={{ textAlign: align }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: align === 'right' ? 'flex-end' : 'flex-start' }}>
        {align === 'left' && <ClanEmblem emblem={side.emblem} size={26} />}
        <div className="clan-row__sub" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {side.name} [{side.tag}]
        </div>
        {align === 'right' && <ClanEmblem emblem={side.emblem} size={26} />}
      </div>
      <div className={`clan-war__score${highlight ? ' is-win' : ''}`}>{side.score}</div>
    </div>
  );
}

function RequestRow({ req, t }: { req: NonNullable<ClanDetailDTO['joinRequests']>[number]; t: T }) {
  const approve = useClanStore((s) => s.approve);
  const reject = useClanStore((s) => s.reject);
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    try { await fn(); } catch (e) { toastErr(e, t); } finally { setBusy(false); }
  };
  return (
    <div className="clan-member">
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 13 }}>{req.displayName}</div>
        <div className="clan-row__sub">{req.mmr} MMR{req.message ? ` · “${req.message}”` : ''}</div>
      </div>
      <button className="clan-act" disabled={busy} onClick={() => void run(() => approve(req.userId))} style={{ color: 'var(--c-cyan)' }}>✓</button>
      <button className="clan-act is-danger" disabled={busy} onClick={() => void run(() => reject(req.userId))}>✕</button>
    </div>
  );
}

function MemberRow({ member, myRole, t }: { member: ClanMemberDTO; myRole: ClanRole; t: T }) {
  const me = useAuthStore((s) => s.user);
  const kick = useClanStore((s) => s.kick);
  const setRole = useClanStore((s) => s.setRole);
  const transfer = useClanStore((s) => s.transfer);
  const [busy, setBusy] = useState(false);
  const isMe = me?.id === member.userId;
  const myRank = ROLE_RANK[myRole];
  const tgtRank = ROLE_RANK[member.role];
  const canModerate = !isMe && myRank >= ROLE_RANK.elder && myRank > tgtRank;
  const canSetRoles = !isMe && myRank > tgtRank;

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    try { await fn(); } catch (e) { toastErr(e, t); } finally { setBusy(false); }
  };

  return (
    <div className="clan-member">
      <Avatar color={member.avatarColor} size={36} ring={false} photoUrl={member.avatarUrl} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {member.displayName}{isMe ? ` ${t('clan.you')}` : ''}
          </span>
          <span className={`role-badge role-badge--${member.role}`}>{t(ROLE_KEY[member.role])}</span>
        </div>
        <div className="clan-row__sub">🏆 {member.warPoints} · {member.mmr} MMR</div>
      </div>

      {(canModerate || canSetRoles) && (
        <div style={{ display: 'flex', gap: 4 }}>
          {myRole === 'leader' && member.role === 'elder' && (
            <ActBtn busy={busy} label="↑Co" onClick={() => run(() => setRole(member.userId, 'coLeader'))} />
          )}
          {myRank >= ROLE_RANK.coLeader && member.role === 'member' && (
            <ActBtn busy={busy} label="↑Eld" onClick={() => run(() => setRole(member.userId, 'elder'))} />
          )}
          {myRole === 'leader' && member.role === 'coLeader' && (
            <ActBtn busy={busy} label="↓Eld" onClick={() => run(() => setRole(member.userId, 'elder'))} />
          )}
          {myRank >= ROLE_RANK.coLeader && member.role === 'elder' && (
            <ActBtn busy={busy} label="↓Mem" onClick={() => run(() => setRole(member.userId, 'member'))} />
          )}
          {myRole === 'leader' && (
            <ActBtn busy={busy} label="👑" onClick={async () => {
              if (await confirm({ body: t('clan.confirm_make_leader', { name: member.displayName }) })) void run(() => transfer(member.userId));
            }} />
          )}
          {canModerate && (
            <ActBtn busy={busy} label="✕" danger onClick={async () => {
              if (await confirm({ body: t('clan.confirm_kick', { name: member.displayName }), danger: true })) void run(() => kick(member.userId));
            }} />
          )}
        </div>
      )}
    </div>
  );
}

function ActBtn({ label, onClick, busy, danger }: { label: string; onClick: () => void; busy: boolean; danger?: boolean }) {
  return (
    <button className={`clan-act${danger ? ' is-danger' : ''}`} disabled={busy} onClick={onClick}>{label}</button>
  );
}

function LeaderboardView({ t }: { t: T }) {
  const myClan = useClanStore((s) => s.myClan);
  const [items, setItems] = useState<ClanLeaderboardEntry[] | null>(null);
  const [season, setSeason] = useState<ClanSeasonDTO | null>(null);

  useEffect(() => {
    clanApi.leaderboard().then((r) => { setItems(r.items); setSeason(r.season); }).catch((e) => toastErr(e, t));
  }, []);

  const myId = myClan?.id;
  return (
    <div className="clan-scroll">
      {season && (
        <div className="clan-card" style={{ padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span className="display" style={{ fontSize: 15, color: 'var(--c-yellow)' }}>{t('clan.season', { n: season.number })}</span>
          <span style={{ fontSize: 12, color: 'var(--ink-muted)' }}>{fmtLeft(season.endsAt, t)}</span>
        </div>
      )}
      {items === null ? (
        <div style={{ color: 'var(--ink-muted)', padding: 16 }}>{t('common.loading')}</div>
      ) : items.length === 0 ? (
        <div style={{ color: 'var(--ink-muted)', padding: 24, textAlign: 'center' }}>{t('clan.lb_empty')}</div>
      ) : (
        items.map((c) => {
          const mine = c.id === myId;
          const medal = c.rankIndex === 1 ? '🥇' : c.rankIndex === 2 ? '🥈' : c.rankIndex === 3 ? '🥉' : `${c.rankIndex}`;
          return (
            <div key={c.id} className={`clan-row${mine ? ' is-mine' : ''}`}>
              <span className={`clan-medal${c.rankIndex === 1 ? ' clan-medal--1' : ''}`}>{medal}</span>
              <ClanEmblem emblem={c.emblem} size={36} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div><span className="clan-row__name">{c.name}</span> <span className="clan-row__tag">[{c.tag}]</span></div>
                <div className="clan-row__sub">👥 {c.memberCount}/{c.maxMembers}</div>
              </div>
              <span className="clan-chip is-gold" style={{ fontSize: 13 }}>🏆 {c.trophies}</span>
            </div>
          );
        })
      )}
    </div>
  );
}

function ClanEmblem({ emblem, size, tappable, onClick }: { emblem: string | null; size: number; tappable?: boolean; onClick?: () => void }) {
  const isImg = isImageAssetUrl(emblem);
  return (
    <div
      className={`clan-emblem${tappable ? ' is-tappable' : ''}`}
      onClick={onClick}
      style={{ width: size, height: size, fontSize: size * 0.5 }}
    >
      {isImg ? (
        <img src={resolveAssetUrl(emblem) ?? emblem!} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        <span>{emblem || '🛡️'}</span>
      )}
    </div>
  );
}
